import React from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {ConsoleLogger} from 'aws-amplify/utils'
import Layout from 'components/layouts/WelcomePageLayout'
import LoaderButton from 'components/atoms/LoaderButton'
import {useAuthentication} from 'components/providers/AuthenticationProvider'
import {Formik} from 'formik'
import * as Yup from 'yup'
import Auth from 'lib/auth'
import Typography from '@material-ui/core/Typography'
import {makeStyles} from '@material-ui/core/styles'
import {TextField} from '@material-ui/core'
import {passwordSchema} from 'lib/schemas'

const logger = new ConsoleLogger('CompleteNewPasswordForm')

const useStyles = makeStyles(theme => ({
  form: {
    width: '100%', // Fix IE 11 issue.
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}))

const NewPasswordSchema = Yup.object().shape({
  password: passwordSchema,
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'formValidations.passwordDoesntMatch')
    .required('formValidations.required'),
})

export default function CompleteNewPasswordForm() {
  const classes = useStyles()
  const intl = useIntl()

  const {user, signIn} = useAuthentication()

  async function handleSubmit(values) {
    try {
      await Auth.completeNewPassword(user, values.password)
      await signIn()
    } catch (e) {
      logger.error(e)
    }
  }

  return (
    <Layout header={Header}>
      <Formik
        validationSchema={NewPasswordSchema}
        initialValues={{password: '', confirmPassword: ''}}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className={classes.form} noValidate>
            <TextField
              type="password"
              id="password"
              name="password"
              margin="normal"
              variant="outlined"
              fullWidth
              required
              label={intl.formatMessage({id: 'password'})}
              value={values.password}
              autoComplete="new-password"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.password && Boolean(errors.password)}
              helperText={
                touched.password &&
                errors.password &&
                intl.formatMessage({id: errors.password})
              }
            />
            <TextField
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              margin="normal"
              variant="outlined"
              fullWidth
              required
              label={intl.formatMessage({id: 'confirmPassword'})}
              value={values.confirmPassword}
              autoComplete="new-password"
              onChange={handleChange}
              onBlur={handleBlur}
              error={touched.confirmPassword && Boolean(errors.confirmPassword)}
              helperText={
                touched.confirmPassword &&
                errors.confirmPassword &&
                intl.formatMessage({id: errors.confirmPassword})
              }
            />
            <LoaderButton
              type="submit"
              isLoading={isSubmitting}
              loadingText={intl.formatMessage({id: 'login.savingPassword'})}
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
            >
              {intl.formatMessage({id: 'login.savePassword'})}
            </LoaderButton>
          </form>
        )}
      </Formik>
    </Layout>
  )
}

const Header = ({className}) => (
  <div className={className}>
    <Typography variant="h5" component="h1">
      <FormattedMessage id="login.greeting" />
    </Typography>
    <Typography variant="body2">
      <FormattedMessage id="login.requirePasswordChange" />
    </Typography>
  </div>
)
